import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { BenefitProposalData } from '~/data/proposals';
import {
  CommonEmployeeProposalFormData,
  ProposalAttachmentFormData,
  mapDefaultDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { convertToDateOrNull } from '~/utils/helpers/date';

export type BenefitProposalFormData = CommonEmployeeProposalFormData & {
  benefitType: string;
  benefitFrequency: string;
  startDate: Date | null;
  endDate?: Date | null;
  benefitAmount: number;
  taxableAmount: number;
  info: string;
} & ProposalAttachmentFormData;

export const mapDefaultData = (
  targetEmploymentId: number,
  proposalData?: BenefitProposalData,
  defaultResponsibleHrId?: number,
): BenefitProposalFormData => {
  const { defaultToEmptyString, defaultResponsibleHR, defaultToNumber } =
    mapDefaultDataUtils();

  return {
    employmentId: defaultToEmptyString(
      proposalData?.employmentFK || targetEmploymentId,
    ),
    benefitType: defaultToEmptyString(proposalData?.benefitTypeFK),
    benefitFrequency: defaultToEmptyString(proposalData?.frequencyFK),
    startDate: convertToDateOrNull(proposalData?.startDate),
    endDate: convertToDateOrNull(proposalData?.endDate),
    benefitAmount: defaultToNumber(proposalData?.amount, 0),
    taxableAmount: defaultToNumber(proposalData?.taxableAmount, 0),
    info: defaultToEmptyString(proposalData?.info),
    attachmentId: defaultToNumber(proposalData?.attachmentId, -1),
    attachmentName: defaultToEmptyString(proposalData?.attachmentName),
    attachment: undefined,
    responsibleHR: defaultResponsibleHR(
      defaultResponsibleHrId,
      defaultResponsibleHrId,
    ),
  };
};

export const useValidationSchema = (isEmploymentIdRequired: boolean) => {
  const { t } = useTranslation();

  return yup.object().shape<BenefitProposalFormData>({
    employmentId: isEmploymentIdRequired
      ? yup.string().required(t('fieldIsRequired'))
      : yup.string().default(''),
    benefitType: yup.string().required(t('fieldIsRequired')),
    benefitFrequency: yup.string().required(t('fieldIsRequired')),
    startDate: yup.date().nullable().required(t('fieldIsRequired')),
    endDate: yup.date().nullable(),
    benefitAmount: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(),
    taxableAmount: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(),
    info: yup.string(),
    responsibleHR: yup.number().required(t('fieldIsRequired')),
    attachment: yup.object<File>().nullable(),
    attachmentId: yup.number(),
    attachmentName: yup.string(),
  });
};
